import { useState } from 'react'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import { useAuthStore } from '../../stores/authStore'

const industries = [
  { value: 'restaurant', label: 'Restaurant / Bar' },
  { value: 'retail', label: 'Retail Store' },
  { value: 'salon', label: 'Salon / Spa' },
  { value: 'auto_repair', label: 'Auto Repair Shop' },
  { value: 'fitness', label: 'Fitness Center' },
]

export default function Settings() {
  const { user, activeTenant } = useAuthStore()
  const [formData, setFormData] = useState({
    businessName: activeTenant?.businessName || '',
    email: user?.email || '',
    industry: activeTenant?.industry || 'restaurant',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  })
  const [saving, setSaving] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    // Simulate save
    setTimeout(() => {
      setSaving(false)
      alert('Settings saved!')
    }, 1000)
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your business profile and preferences</p>
      </div>

      {/* Business Profile */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Business Profile</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label htmlFor="businessName" className="label">
                Business Name
              </label>
              <input
                id="businessName"
                name="businessName"
                type="text"
                value={formData.businessName}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="industry" className="label">
                Industry
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="input"
              >
                {industries.map((ind) => (
                  <option key={ind.value} value={ind.value}>
                    {ind.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label htmlFor="address" className="label">
                Address
              </label>
              <input
                id="address"
                name="address"
                type="text"
                value={formData.address}
                onChange={handleChange}
                className="input"
                placeholder="123 Main St"
              />
            </div>

            <div>
              <label htmlFor="city" className="label">
                City
              </label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="state" className="label">
                  State
                </label>
                <input
                  id="state"
                  name="state"
                  type="text"
                  value={formData.state}
                  onChange={handleChange}
                  className="input"
                />
              </div>
              <div>
                <label htmlFor="zipCode" className="label">
                  ZIP Code
                </label>
                <input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit" loading={saving}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>

      {/* Locations */}
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Locations</h2>
          <Button variant="secondary" size="sm">
            Add Location
          </Button>
        </div>
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
          <div className="p-4 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">Main Location</p>
              <p className="text-sm text-gray-500">Primary location</p>
            </div>
            <Button variant="ghost" size="sm">
              Edit
            </Button>
          </div>
        </div>
      </Card>

      {/* Integrations */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Integrations</h2>
        <div className="space-y-4">
          {/* Square - Connected */}
          <div className="flex items-center justify-between p-4 border border-green-200 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Square POS</p>
                <p className="text-sm text-green-600">Connected • Syncing daily</p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          </div>

          {/* DoorDash - Connected */}
          <div className="flex items-center justify-between p-4 border border-green-200 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">DD</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">DoorDash</p>
                <p className="text-sm text-green-600">Connected • Weekly payouts</p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          </div>

          {/* Uber Eats - Connected */}
          <div className="flex items-center justify-between p-4 border border-green-200 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">UE</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Uber Eats</p>
                <p className="text-sm text-green-600">Connected • Weekly payouts</p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          </div>

          {/* Grubhub - Connected */}
          <div className="flex items-center justify-between p-4 border border-green-200 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">GH</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Grubhub</p>
                <p className="text-sm text-green-600">Connected • Weekly payouts</p>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          </div>

          {/* Bank - Available */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-lg">🏦</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Bank Account (Plaid)</p>
                <p className="text-sm text-gray-500">Connect for automatic transaction import</p>
              </div>
            </div>
            <Button variant="secondary" size="sm">
              Connect
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-400 mt-4">
          Integrations are simulated in demo mode with realistic data patterns.
        </p>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">Delete Account</p>
            <p className="text-sm text-gray-500">
              Permanently delete your account and all data
            </p>
          </div>
          <Button variant="danger" size="sm">
            Delete Account
          </Button>
        </div>
      </Card>
    </div>
  )
}
